/* eslint-disable camelcase */
import React from 'react';
import { compose } from 'redux';
import { withRouter, Link } from 'react-router';
import PropTypes from 'prop-types';
import { stripePayouts, stripeBalanceTransactions } from 'ducks';
import { formatCurrency, formatDateTime, titleize } from 'utils';
import { sum } from 'utils/arrays';
import { duckify, filterDuck, singleDuck } from 'utils/duckify';
import FontAwesome from 'utils/FA';
import groupBy from 'utils/groupBy';
import Placeholder from 'components/Placeholder';
import FlashMessage from 'components/FlashMessage/FlashMessage';
import withFiltering from 'hocs/withFiltering';
import './styles/StripePayout.scss';
import { BalanceTransactionType, PayoutType } from '../../propTypes';
import BulkAction from "../ContactList/BulkActions/BulkAction";
import { faSignOut } from "@fortawesome/pro-solid-svg-icons/faSignOut";
import BulkExportAction from "../ContactList/BulkActions/BulkExportAction";
import BulkActions from "../ContactList/BulkActions/BulkActions";
import ContactList from '../ContactList/ContactList';
import { currencyColumn, defaultColumn, expanderColumn, linkColumn } from '../ContactList/ColumnDefinitions';

const StripePayoutOverview = ({ payout, errorMessage }) => (
  <React.Fragment>
    {errorMessage && <FlashMessage message={errorMessage} type='danger' />}
    <h3>Overview</h3>

    <div className='stripe-payout-overview'>
      <div className='payout-status'>
        <div className='stripe-payout-overview-header'>Status</div>
        <div className='stripe-payout-overview-value'>{titleize(payout.status)}</div>
      </div>
      <div className='payout-date'>
        <div className='stripe-payout-overview-header'>Date Deposited</div>
        <div className='stripe-payout-overview-value'>{formatDateTime(payout.created, 'fns_date')}</div>
      </div>
      <div className='payout-amount'>
        <div className='stripe-payout-overview-header'>Amount</div>
        <div className='stripe-payout-overview-value'>{formatCurrency(payout.amount)}</div>
      </div>
      <div className='payout-id'>
        <div className='stripe-payout-overview-header'>Payout ID</div>
        <div className='stripe-payout-overview-value'>{payout.id}</div>
      </div>
      <div className='payout-description'>
        <div className='stripe-payout-overview-header'>Description</div>
        <div className='stripe-payout-overview-value'>{payout.description}</div>
      </div>
    </div>
  </React.Fragment>
);

StripePayoutOverview.propTypes = {
  payout: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    created: PropTypes.string,
    amount: PropTypes.string,
    description: PropTypes.string
  }).isRequired,
  errorMessage: PropTypes.string
};

StripePayoutOverview.defaultProps = {
  errorMessage: ''
};

function typeMatch(item) {
  if (item.fee_type) {
    return item.fee_type;
  }

  return item.type;
}

const StripeBalanceTransactionsSummary = ({ balanceTransactions }) => {
  const transactionGroups = groupBy(balanceTransactions, typeMatch);
  return (
    <React.Fragment>
      <h3>Summary</h3>
      <table className='table stripe-transaction-summary'>
        <thead>
          <tr>
            <th />
            <th>Count</th>
            <th>Gross</th>
            <th>Fees</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(transactionGroups).map(([key, group]) => (
            <tr key={key}>
              <td>{titleize(key)}</td>
              <td>{group.length}</td>
              <td>{formatCurrency(sum(group.map(item => parseFloat(item.amount))))}</td>
              <td>{formatCurrency(sum(group.map(item => parseFloat(item.fee))))}</td>
              <td>{formatCurrency(sum(group.map(item => parseFloat(item.net))))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </React.Fragment>
  );
};

StripeBalanceTransactionsSummary.propTypes = {
  balanceTransactions: PropTypes.arrayOf(PropTypes.object).isRequired
};

const transactionColumns = [
  expanderColumn({}),
  defaultColumn({
    header: 'Transaction ID',
    value: 'transaction_id',
    subRowValue: ''
  }),
  linkColumn({
    header: 'Order ID',
    value: (balance_transaction) => balance_transaction.order && balance_transaction.order.id,
    link: (balance_transaction) => balance_transaction.order && `/setup/manage_orders?search[id]=${balance_transaction.order.id}`,
    subRowValue: (balance_transaction) => ''
  }),
  defaultColumn({
    header: 'Date / Time',
    value: (balance_transaction) => <td>{formatDateTime(balance_transaction.created)}</td>,
    subRowValue: ''
  }),
  defaultColumn({
    header: 'Payer Name',
    value: (balance_transaction) => (
      <td>
        {balance_transaction.order && balance_transaction.order.user
          && `${balance_transaction.order.user.first_name} ${balance_transaction.order.user.last_name}`}
      </td>
    ),
    subRowValue: ''
  }),
  defaultColumn({
    header: 'Type',
    value: (balance_transaction) => <td>{balance_transaction.order && balance_transaction.order.entity_class}</td>,
    subRowValue: (itemAmount) => <td>{itemAmount.billing_category.name}</td>
  }),
  defaultColumn({
    header: 'Type Details',
    value: 'fee_type',
    subRowValue: ''
  }),
  defaultColumn({
    header: 'Original Order',
    value: (balance_transaction) => (
      balance_transaction.fee_source_id
        ? (
          <td>
            <a href={`/setup/manage_orders?search[id]=${balance_transaction.fee_source_id}`}>
              {balance_transaction.fee_source_id}
            </a>
          </td>
        )
        : <td>{balance_transaction.fee_source_id}</td>
    )
  }),
  currencyColumn({
    header: 'Base Amount',
    value: 'base_amount',
    subRowValue: 'amount'
  }),
  currencyColumn({
    header: 'Convenience Fee',
    value: 'convenience_fee',
    subRowValue: ''
  }),
  currencyColumn({
    header: 'Transaction Amount',
    value: 'transaction_amount',
    subRowValue: ''
  }),
  currencyColumn({
    header: 'Platform Fee',
    value: 'platform_fee',
    subRowValue: ''
  }),
  currencyColumn({
    header: 'Merchant Fee',
    value: 'merchant_fee',
    subRowValue: ''
  }),
  currencyColumn({
    header: 'Payout Amount',
    value: 'net',
    subRowValue: ''
  }),
  defaultColumn({
    header: 'Student(s)',
    value: 'student_name',
    subRowValue: (itemAmount) => (
      itemAmount.billing_contact
        ? (
          <td className='contact-list-react-name'>
            <a href={`/contacts/${itemAmount.billing_contact.contact.id}`}>
              {`${itemAmount.billing_contact.contact.first_name} ${itemAmount.billing_contact.contact.last_name}`}
            </a>
          </td>
        )
        : <td />
    )
  }),
  defaultColumn({
    header: 'Term',
    value: (balance_transaction) => <td>{balance_transaction.school_year && balance_transaction.school_year.name}</td>,
    subRowValue: (itemAmount) => (
      <td>
        {
          itemAmount.billing_contact
          && itemAmount.billing_contact.school_year
          && itemAmount.billing_contact.school_year.name
        }
      </td>
    )
  })
];

const StripeBalanceTransactionsDetailsList = ({ balanceTransactions }) => {
  // remove the payout line
  const filteredTransactions = balanceTransactions.filter((transaction) => transaction.type !== 'payout');
  return (
    <React.Fragment>
      <h3>Detail</h3>
      <ContactList
        className='stripe-balance-transactions'
        columns={transactionColumns}
        contacts={filteredTransactions}
        getSubRowContacts={(contact) => contact.billing_item_amounts}
      />
    </React.Fragment>
  );
};

StripeBalanceTransactionsDetailsList.propTypes = {
  balanceTransactions: PropTypes.arrayOf(PropTypes.object).isRequired
};

const StripePayout = (props) => {
  const {
    params: { payoutId },
    payout,
    balanceTransactions,
    duckMeta,
    location,
    dispatch
  } = props;

  const summaryPath = location.pathname.replace(new RegExp(`/${payoutId}/?`), '');

  const downloadMessage = 'Your file is being prepared. You can safely close this modal and continue working. The '
    + 'report will automatically download when completed and will also be available on the User Jobs page.';

  return (
    <div className={'stripe-payout-detail'}>
      <h2>
        {`Payout Report - ${payoutId}`}
      </h2>
      <div className={'stripe-payout-detail__header'}>
        <Link to={`${summaryPath}${location.search}`}>
          <FontAwesome name='caret-left' nbsp />
          Return to Payout Summary Page
        </Link>

        <BulkActions>
          <BulkAction title='Export' icon={faSignOut}>
            <BulkExportAction
              dispatch={dispatch}
              title='Export to CSV'
              location={location}
              url='/api/stripe_balance_transactions/export.csv'
              message={downloadMessage}
              opts={payout.id}
            />
            <BulkExportAction
              dispatch={dispatch}
              title='Export to PDF'
              location={location}
              url='/api/stripe_balance_transactions/export.pdf'
              message={downloadMessage}
              opts={payout.id}
            />
            <BulkExportAction
              dispatch={dispatch}
              title='Remittance Export to CSV'
              location={location}
              url='/api/stripe_balance_transactions/simplified_export.csv'
              message={downloadMessage}
              opts={payout.id}
            />
            <BulkExportAction
              dispatch={dispatch}
              title='Remittance Export to PDF'
              location={location}
              url='/api/stripe_balance_transactions/simplified_export.pdf'
              message={downloadMessage}
              opts={payout.id}
            />
          </BulkAction>
        </BulkActions>
      </div>

      <Placeholder.text ready={duckMeta.payout.ready} width={500} lines={10}>
        <React.Fragment>
          <StripePayoutOverview
            payout={payout}
            errorMessage={payout.errorMessage || balanceTransactions.errorMessage}
          />

          <Placeholder.text ready={duckMeta.balanceTransactions.ready} width={500} lines={5}>
            <React.Fragment>
              <StripeBalanceTransactionsSummary balanceTransactions={balanceTransactions} />
              <StripeBalanceTransactionsDetailsList balanceTransactions={balanceTransactions} />
            </React.Fragment>
          </Placeholder.text>
        </React.Fragment>
      </Placeholder.text>
    </div>
  );
};

StripePayout.propTypes = {
  params: PropTypes.shape({ payoutId: PropTypes.string }).isRequired,
  payout: PayoutType.isRequired,
  balanceTransactions: PropTypes.arrayOf(BalanceTransactionType).isRequired,
  duckMeta: PropTypes.shape({
    payout: PropTypes.shape({ ready: PropTypes.bool }),
    balanceTransactions: PropTypes.shape({ ready: PropTypes.bool })
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string
  }).isRequired
};

const mapDucksToProps = ({ params: { payoutId } }) => ({
  payout: singleDuck(stripePayouts, payoutId),
  balanceTransactions: filterDuck(stripeBalanceTransactions, { stripe_payout_id: payoutId })
});

const mapStateToProps = () => ({});

const mapDispatchToProps = () => ({});

export default compose(
  withRouter,
  withFiltering,
  duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)
)(StripePayout);
